import React, { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';

const INPUT = 'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// "How much of this deal/quotation to invoice now?" — the whole thing, a
// percentage, a fixed amount, or whatever is still left. Amounts are pre-tax;
// tax is added on the invoice as usual.
export default function BillingSliceModal({ title, total, billed = 0, currency, submitting, onClose, onSubmit }) {
  const remaining = r2(total - billed);
  const [mode, setMode] = useState(billed > 0 ? 'remaining' : 'full');
  const [percent, setPercent] = useState(50);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');

  const preview = mode === 'full' ? total : mode === 'remaining' ? remaining
    : mode === 'percent' ? r2(total * (Number(percent) || 0) / 100) : r2(amount);
  const over = preview > remaining + 0.01;
  const modes = [
    ['full', 'Full amount', billed > 0],
    ['percent', 'Percentage', false],
    ['amount', 'Fixed amount', false],
    ['remaining', 'Remaining', billed <= 0],
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[['Value', total], ['Invoiced', billed], ['Left to bill', remaining]].map(([k, v]) => (
              <div key={k} className="bg-[#F7F9FB] border border-[#D8DDE6] rounded p-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[#706E6B]">{k}</p>
                <p className="text-[13px] font-bold tabular-nums">{formatCurrency(v, currency)}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="How much to invoice">
            {modes.map(([id, l, disabled]) => (
              <button type="button" key={id} role="radio" aria-checked={mode === id} disabled={disabled} onClick={() => setMode(id)}
                className={cn('h-9 rounded border text-[12px] font-semibold disabled:opacity-40', mode === id ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{l}</button>
            ))}
          </div>
          {mode === 'percent' && (
            <label className="block"><span className="block text-[11px] font-semibold text-[#706E6B] mb-1">Percent of the value</span>
              <input aria-label="Percent of the value" type="number" min="1" max="100" step="0.01" className={INPUT} value={percent} onChange={(e) => setPercent(e.target.value)} /></label>
          )}
          {mode === 'amount' && (
            <label className="block"><span className="block text-[11px] font-semibold text-[#706E6B] mb-1">Amount (before tax)</span>
              <input aria-label="Amount before tax" type="number" min="0" step="0.01" className={INPUT} value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          )}
          {mode !== 'full' && (
            <label className="block"><span className="block text-[11px] font-semibold text-[#706E6B] mb-1">Label on the invoice (optional)</span>
              <input aria-label="Label on the invoice" className={INPUT} value={label} placeholder="e.g. Advance payment, Milestone 2" onChange={(e) => setLabel(e.target.value)} /></label>
          )}
          <p className={cn('text-[13px]', over ? 'text-[#BA0517]' : 'text-[#2E2E2E]')}>
            {over ? `Only ${formatCurrency(remaining, currency)} is left to invoice.` : <>This invoice: <strong className="tabular-nums">{formatCurrency(preview, currency)}</strong> + tax</>}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Cancel</button>
          <button type="button" disabled={submitting || over || !(preview > 0)}
            onClick={() => onSubmit({ mode, percent: Number(percent), amount: Number(amount), label: label.trim() || undefined })}
            className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-50">{submitting ? 'Creating…' : 'Create invoice'}</button>
        </div>
      </div>
    </div>
  );
}
